import { delMany, get, keys, set } from "idb-keyval";

const META_STORE_PREFIX = "ruralops-offline-meta";

export type DatasetMeta = {
	datasetId: string;
	scope: string;
	lastSyncedAt: number;
	count: number;
	estimatedBytes: number;
	lastError?: string;
};

function metaKey(datasetId: string, scope: string) {
	return `${META_STORE_PREFIX}:${datasetId}:${scope}`;
}

const metaListeners = new Set<() => void>();

/** Calls `listener` after every `setDatasetMeta` write. Listeners must not throw. */
export function subscribeDatasetMeta(listener: () => void): () => void {
	metaListeners.add(listener);
	return () => {
		metaListeners.delete(listener);
	};
}

export async function setDatasetMeta(meta: DatasetMeta): Promise<void> {
	await set(metaKey(meta.datasetId, meta.scope), meta);
	for (const listener of metaListeners) listener();
}

async function readOrgMetas(
	datasetId: string,
	organizationId: string,
): Promise<DatasetMeta[]> {
	const allKeys = await keys<string>();
	const orgKey = metaKey(datasetId, `org:${organizationId}`);
	const relevant = allKeys.filter(
		(k) => k === orgKey || k.startsWith(`${orgKey}:`),
	);
	const metas = await Promise.all(relevant.map((k) => get<DatasetMeta>(k)));
	return metas.filter((m): m is DatasetMeta => m !== undefined);
}

/**
 * Returns the most recent successful lastSyncedAt across the org's scopes (org-wide and per-establishment) for a dataset.
 * A meta with `lastError` records a failed or over-cap prepare, which leaves no copy behind.
 */
export async function getLatestSyncAtForDataset(
	datasetId: string,
	organizationId: string,
): Promise<number | null> {
	let latest: number | null = null;
	for (const m of await readOrgMetas(datasetId, organizationId)) {
		if (
			m.lastSyncedAt &&
			!m.lastError &&
			(!latest || m.lastSyncedAt > latest)
		) {
			latest = m.lastSyncedAt;
		}
	}
	return latest;
}

/**
 * Returns the scopes of the org's dataset that hold a usable copy. A meta with
 * `lastError` (a failed fetch or an over-cap result) means the scope cached nothing.
 */
export async function getPreparedScopes(
	datasetId: string,
	organizationId: string,
): Promise<Set<string>> {
	const metas = await readOrgMetas(datasetId, organizationId);
	return new Set(metas.filter((m) => !m.lastError).map((m) => m.scope));
}

export async function clearDatasetMeta(): Promise<void> {
	const allKeys = await keys<string>();
	await delMany(allKeys.filter((k) => k.startsWith(`${META_STORE_PREFIX}:`)));
}
